import React, { useState, useEffect } from 'react';
import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent } from '../services/supabase';
import { Mail, Loader2, CheckCircle2, AlertCircle, ArrowRight, Check, X } from 'lucide-react';

interface LoginProps {
  onAuthSuccess: () => void;
  onClose?: () => void;
  initialMode?: 'login' | 'signup';
}

export default function Login({ onAuthSuccess, onClose, initialMode = 'login' }: LoginProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) localStorage.setItem('referral_code', refCode);
      try {
        document.cookie = `rehub_referral_code=${encodeURIComponent(refCode)}; path=/; max-age=${60 * 60 * 24 * 14}; SameSite=Lax`;
      } catch {}
  }, []);

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      return;
    }
    if (!isConfigured) {
      setError('System Error: Supabase connection not configured.');
      return;
    }
    setGoogleLoading(true);
    setError(null);
    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Google Sign-In Error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      setLoading(false);
      return;
    }
    if (website.trim()) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }
    if (!isConfigured) {
      setError('System Error: Supabase connection not configured. Missing API Keys.');
      setLoading(false);
      return;
    }

    try {
      const rateLimitCheck = await checkRateLimits(email);
      if (!rateLimitCheck.allowed) {
        setError(rateLimitCheck.message || 'Too many attempts. Please try again later.');
        setLoading(false);
        return;
      }

      if (mode === 'signup' && acceptedMarketing) {
        await storePendingMarketingConsent(email);
      }
      if (mode === 'signup') {
        localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: { signup_app: 'showcase' },
        },
      });
      if (error) throw error;

      await logEvent(
        'magic_link_requested',
        null,
        email,
        null,
        navigator.userAgent,
        { mode, accepted_marketing: acceptedMarketing, source_app: 'showcase' }
      );

      setSent(true);
    } catch (err: any) {
      console.error('Auth Error:', err);
      if (err.message === 'Failed to fetch' || !navigator.onLine) {
        setError('Connection failed. Please check your internet or firewall settings.');
      } else if (err.status === 400) {
        setError('Invalid email address or request.');
      } else {
        setError(err.message || 'Failed to send login link');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
    setSent(false);
    setAcceptedTerms(false);
    setAcceptedMarketing(false);
    setWebsite('');
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center p-6 animate-fade-in w-full min-h-[80vh]">
        <div className="max-w-md w-full bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center shadow-2xl">
          <div className="flex flex-col items-center gap-3 mb-6">
            <img src="/Logo.png" alt="Showcase by ReHub" className="h-10 w-auto max-w-[180px] object-contain" />
          </div>
          <div className="w-16 h-16 bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Check your email</h2>
          <p className="text-slate-400 mb-6">
            We've sent a magic link to <span className="text-slate-200 font-medium">{email}</span>.
            <br />Click it to {mode === 'signup' ? 'create your account' : 'log in'}.
          </p>
          <button
            onClick={() => setSent(false)}
            className="text-cyan-400 hover:text-cyan-300 font-medium text-sm transition-colors"
          >
            Try a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 animate-fade-in w-full">
      <div className="max-w-md w-full bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl z-10 relative">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        )}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-100 mb-2">
            {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-slate-400">
            {mode === 'signup'
              ? 'Create beautiful property showcases in minutes.'
              : 'Sign in to manage your property showcases.'}
          </p>
        </div>

        <div className="mb-6 space-y-3 text-left bg-slate-800/50 p-3 rounded-xl border border-slate-800">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center pt-0.5">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border checked:bg-cyan-600 checked:border-cyan-600 transition-all"
              />
              <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
            </div>
            <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
              I accept the <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline hover:text-cyan-300 font-medium" onClick={(e) => e.stopPropagation()}>Terms and Conditions</a> and <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline hover:text-cyan-300 font-medium" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
            </span>
          </label>

          {mode === 'signup' && (
            <label className="flex items-start gap-3 cursor-pointer group animate-fade-in">
              <div className="relative flex items-center pt-0.5">
                <input
                  type="checkbox"
                  checked={acceptedMarketing}
                  onChange={(e) => setAcceptedMarketing(e.target.checked)}
                  className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 appearance-none border checked:bg-cyan-600 checked:border-cyan-600 transition-all"
                />
                <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
              </div>
              <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                I agree to receive marketing, offers, and updates from ReHub Studio. <span className="text-slate-500 text-[10px]">(Optional)</span>
              </span>
            </label>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm mb-6 animate-shake">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-4">
          <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-slate-300">
              Email Address
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-cyan-500 transition-colors">
                <Mail size={18} />
              </div>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@agency.com"
                className="block w-full pl-10 pr-3 py-3 bg-black border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-cyan-900/20 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                {mode === 'signup' ? 'Create Account' : 'Sign In'}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700"></div></div>
          <div className="relative flex justify-center text-sm"><span className="px-3 bg-slate-900/80 text-slate-500">or continue with</span></div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <Loader2 size={20} className="animate-spin text-gray-600" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="mt-6 pt-6 border-t border-slate-800 text-center">
          <p className="text-slate-400 text-sm">
            {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
          </p>
          <button
            onClick={toggleMode}
            className="text-cyan-400 hover:text-cyan-300 font-medium text-sm mt-1 transition-colors hover:underline"
          >
            {mode === 'signup' ? 'Log in here' : 'Sign up for free'}
          </button>
        </div>
      </div>
    </div>
  );
}
